import { memo, useRef, type ReactNode } from "react";
import { type Editor, useEditorState } from "@tiptap/react";
import {
  TextB,
  TextItalic,
  TextStrikethrough,
  BlockquoteIcon,
  CodeBlockIcon,
  BulletList,
  ListNumbers,
} from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { IconTooltip } from "@/components/ui/icon-tooltip";
import { cn } from "@/lib/utils";
import { RENDER_DEBUG } from "@/lib/debug-flags";

interface FormattingToolbarProps {
  editor: Editor | null;
}

const formatButtonClass = (active: boolean) =>
  cn(
    "h-7 w-7 rounded",
    active ? "bg-foreground/15 text-foreground" : "text-muted-foreground",
  );

function FormatButton({
  active,
  label,
  onClick,
  children,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <IconTooltip label={label}>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onClick}
        className={formatButtonClass(active)}
        aria-label={label}
      >
        {children}
      </Button>
    </IconTooltip>
  );
}

export const FormattingToolbar = memo(function FormattingToolbar({
  editor,
}: FormattingToolbarProps) {
  const renderCountRef = useRef(0);
  // eslint-disable-next-line react-hooks/refs
  renderCountRef.current += 1;

  const state = useEditorState({
    editor,
    selector: (ctx) =>
      ctx.editor
        ? {
            bold: ctx.editor.isActive("bold"),
            italic: ctx.editor.isActive("italic"),
            strike: ctx.editor.isActive("strike"),
            codeBlock: ctx.editor.isActive("codeBlock"),
            blockquote: ctx.editor.isActive("blockquote"),
            bulletList: ctx.editor.isActive("bulletList"),
            orderedList: ctx.editor.isActive("orderedList"),
          }
        : null,
  });

  if (!editor || !state) return null;

  if (RENDER_DEBUG)
    console.debug(
      // eslint-disable-next-line react-hooks/refs
      `[FormattingToolbar] render #${renderCountRef.current}`,
      `b=${state.bold} i=${state.italic} s=${state.strike} cb=${state.codeBlock} q=${state.blockquote}`,
    );

  return (
    <div className="flex items-center gap-0.5 border-b-2 border-border/50 px-2 py-1.5">
      <FormatButton
        active={state.bold}
        label="Bold (⌘B)"
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <TextB size={16} />
      </FormatButton>
      <FormatButton
        active={state.italic}
        label="Italic (⌘I)"
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <TextItalic size={16} />
      </FormatButton>
      <FormatButton
        active={state.strike}
        label="Strikethrough (⌘⇧X)"
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <TextStrikethrough size={16} />
      </FormatButton>

      <div className="mx-1 h-4 w-px bg-border/50" />

      <FormatButton
        active={state.codeBlock}
        label="Code block (⌘⌥C)"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      >
        <CodeBlockIcon size={16} />
      </FormatButton>
      <FormatButton
        active={state.blockquote}
        label="Blockquote"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <BlockquoteIcon size={16} />
      </FormatButton>

      <div className="mx-1 h-4 w-px bg-border/50" />

      <FormatButton
        active={state.bulletList}
        label="Bullet list"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <BulletList size={16} />
      </FormatButton>
      <FormatButton
        active={state.orderedList}
        label="Numbered list"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListNumbers size={16} />
      </FormatButton>
    </div>
  );
});
